import { memo, useEffect, useState } from "react";
import { GROUPS, groupOfKind, KIND_LABEL } from "../lib/colors";
import { CT_CONTEXT, loadDetail, type BriefConflict, type Detail } from "../lib/data";
import { DEATH_SOURCES, fmtDeaths, fmtInt, SOURCE_NAMES, TIER_LABEL } from "../lib/format";
import { startReplay } from "../lib/replayEngine";
import { useApp } from "../lib/store";
import { durationLabel, yearsLabel } from "../lib/time";

const KINDS = ["interstate", "civil", "colonial", "rebellion", "nonstate", "onesided", "other"];

/** A conflict's page. Memoised: the side panel re-renders with every playhead move, the page need not. */
export const ConflictDetail = memo(function ConflictDetail({ index }: { index: number }) {
  const c = useApp((s) => s.conflicts)!;
  const select = useApp((s) => s.select);
  const setT = useApp((s) => s.setT);
  const setRange = useApp((s) => s.setRange);
  const fly = useApp((s) => s.fly);
  const setEvent = useApp((s) => s.setEvent);
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [allEvents, setAllEvents] = useState(false);
  const [allChildren, setAllChildren] = useState(false);

  useEffect(() => {
    let live = true;
    loadDetail(c.id[index]).then((x) => live && setD(x)).catch((e) => live && setErr(String(e)));
    return () => { live = false; };
  }, [c, index]);

  const kind = c.kind[index], grp = groupOfKind(kind);
  const t0 = c.t0[index], t1 = c.t1[index], og = !!c.og[index];
  const zoom = () => {
    const pad = Math.max((t1 - t0) * 0.15, 0.08);
    setRange(t0 - pad, t1 + pad);
    setT(t0 + (t1 - t0) * 0.02);
    const bb = c.bb[index];
    if (bb) fly({ bbox: bb });
    else if (c.hasPoint[index]) fly({ lon: c.lon[index], lat: c.lat[index], zoom: 4 });
  };
  const jumpTo = (x: BriefConflict) => select(x.i, { jump: true, fly: true });
  const curated = useApp((s) => s.replays?.find((r) => r.i === index));
  const replayable = c.ct[index] !== CT_CONTEXT && t1 - t0 >= 2 / 365;

  return (
    <article className="detail" data-testid="conflict-detail">
      <button className="back" onClick={() => select(-1)}>← All conflicts at this moment</button>
      <h1>{c.name[index]}</h1>
      <div className="chips">
        <span className="chip"><span className="dot" style={{ background: GROUPS[grp].hex }} aria-hidden />{KIND_LABEL[kind]}</span>
        {og && <span className="chip chip-live">ongoing</span>}
        {d?.provisional && <span className="chip">provisional data</span>}
      </div>
      <p className="detail-dates">
        {d?.dates ?? yearsLabel(t0, t1, og)}
        <span className="muted"> · {og ? `${durationLabel(t1 - t0)} so far` : durationLabel(t1 - t0)}</span>
        {d?.end_unknown && <span className="muted"> · end date not recorded</span>}
      </p>
      <div className="detail-actions">
        {replayable && (
          <button className="primary" onClick={() => startReplay({ i: index })} data-testid="replay-war"
            title={curated ? `Play the war with ${curated.ch.length} chapters` : "Play the war at a pace that suits its length"}>
            ▶ Replay
          </button>
        )}
        <button onClick={zoom}>Zoom to this conflict</button>
        {d?.parent && <button onClick={() => jumpTo(d.parent!)}>Part of: {d.parent.name}</button>}
      </div>
      {err && <p className="error">Could not load details: {err}</p>}
      {!d && !err && <p className="loading">Loading…</p>}
      {d && (
        <>
          {d.current && (
            <p className="current">
              <strong>Now:</strong> {TIER_LABEL[d.current.tier] ?? d.current.tier}
              {d.current.fatalities?.["2026"]?.text ? ` · 2026: ${d.current.fatalities["2026"].text}` : ""}
              {d.current.fatalities?.["2025"]?.text ? ` · 2025: ${d.current.fatalities["2025"].text}` : ""}
            </p>
          )}
          {d.extract ? (
            <blockquote className="extract">
              {d.extract}
              <footer>
                From <a href={d.enwiki ?? "#"} target="_blank" rel="noopener">Wikipedia</a>, CC BY-SA 4.0
              </footer>
            </blockquote>
          ) : d.desc ? <p className="desc">{capitalise(d.desc)}</p> : null}

          <dl className="facts">
            {Object.entries(d.deaths).filter(([, v]) => v).length > 0 && (
              <>
                <dt>Deaths</dt>
                <dd>
                  {Object.entries(d.deaths).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><strong>{fmtDeaths(v)}</strong> <span className="muted">{DEATH_SOURCES[k] ?? k}</span></div>
                  ))}
                  <div className="fineprint">Estimates differ in what they count; none is exact.</div>
                </dd>
              </>
            )}
            {d.sides && d.sides.some((s) => s.length) && (
              <>
                <dt>Sides</dt>
                <dd className="sides">
                  <div>{d.sides[0].join(", ")}</div>
                  <div className="vs">vs</div>
                  <div>{d.sides[1].join(", ")}</div>
                </dd>
              </>
            )}
            {!d.sides && d.participants.length > 0 && (
              <><dt>Participants</dt><dd>{d.participants.slice(0, 16).join(", ")}{d.participants.length > 16 ? "…" : ""}</dd></>
            )}
            {d.countries.length > 0 && <><dt>Where</dt><dd>{d.countries.join(", ")}</dd></>}
            {d.group && <><dt>Part of</dt><dd><button className="link" onClick={() => jumpTo(d.group!)}>{d.group.name}</button></dd></>}
          </dl>

          {d.years && Object.keys(d.years).length > 1 && <YearBars years={d.years} />}

          {d.children.length > 0 && (
            <section>
              <h2>{d.ctype === "umbrella" ? "Wars" : "Theatres, campaigns & phases"} <span className="count">{fmtInt(d.n_children)}</span></h2>
              <ul className="brief-list">
                {(allChildren ? d.children : d.children.slice(0, 12)).map((x) => (
                  <li key={x.id}>
                    <button onClick={() => jumpTo(x)}>
                      <span className="dot" style={{ background: GROUPS[groupOfKind(KINDS.indexOf(x.kind ?? "other"))].hex }} aria-hidden />
                      <span className="bl-name">{x.name}</span>
                      <span className="bl-meta">{yearsLabel(x.t0, x.t1, !!x.og)}{x.d ? ` · ${fmtDeaths(x.d)}` : ""}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {!allChildren && d.children.length > 12 && <button className="more" onClick={() => setAllChildren(true)}>Show all {fmtInt(d.children.length)}</button>}
            </section>
          )}
          {d.members.length > 0 && (
            <section>
              <h2>Wars in this series <span className="count">{fmtInt(d.n_members)}</span></h2>
              <ul className="brief-list">
                {d.members.slice(0, 30).map((x) => (
                  <li key={x.id}><button onClick={() => jumpTo(x)}><span className="bl-name">{x.name}</span><span className="bl-meta">{yearsLabel(x.t0, x.t1, !!x.og)}</span></button></li>
                ))}
              </ul>
            </section>
          )}
          {d.events.length > 0 && (
            <section>
              <h2>Battles & events <span className="count">{fmtInt(d.events.length)}{d.n_events > d.events.length ? ` of ${fmtInt(d.n_events)}` : ""}</span></h2>
              <ul className="brief-list events">
                {(allEvents ? d.events : d.events.slice(0, 15)).map((e) => (
                  <li key={e.id}>
                    <button onClick={() => {
                      setT(e.t0 + Math.min(e.t1 - e.t0, 2 / 365) / 2);
                      if (e.x != null && e.y != null) {
                        fly({ lon: e.x, lat: e.y, zoom: 5 });
                        setEvent({ src: "named", name: e.name, date: e.date ?? "", deaths: e.d || null, kind: e.k, conflict: index, lon: e.x, lat: e.y, id: e.id, url: e.u });
                      }
                    }}>
                      <span className="bl-name">{e.name}</span>
                      <span className="bl-meta">{e.date}{e.d ? ` · ${fmtDeaths(e.d)} deaths` : ""}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {!allEvents && d.events.length > 15 && <button className="more" onClick={() => setAllEvents(true)}>Show all {fmtInt(d.events.length)}</button>}
            </section>
          )}
          {d.aliases.length > 0 && <p className="aliases"><span className="muted">Also known as:</span> {d.aliases.join(" · ")}</p>}
          <section className="sources">
            <h2>Sources</h2>
            <ul>
              {d.sources.map((s) => (
                <li key={s.src + s.id}><a href={s.url} target="_blank" rel="noopener">{SOURCE_NAMES[s.src] ?? s.src}{s.src === "wikidata" || s.src === "ucdp" ? ` ${s.id.replace(/^ucdp-/, "#")}` : ""}</a></li>
              ))}
            </ul>
          </section>
        </>
      )}
    </article>
  );
});

function YearBars({ years }: { years: Record<string, number> }) {
  const ys = Object.keys(years).map(Number).sort((a, b) => a - b);
  const y0 = ys[0], y1 = ys[ys.length - 1];
  const vals = Array.from({ length: y1 - y0 + 1 }, (_, k) => years[String(y0 + k)] ?? 0);
  const max = Math.max(...vals, 1);
  return (
    <section>
      <h2>Deaths per year <span className="count">UCDP</span></h2>
      <div className="spark tall" role="img" aria-label={`Recorded deaths per year ${y0}–${y1}`}>
        {vals.map((v, k) => <span key={k} title={`${y0 + k}: ${fmtDeaths(v)}`} style={{ height: `${Math.max(v > 0 ? 3 : 0, (v / max) * 100)}%` }} />)}
      </div>
      <div className="spark-axis"><span>{y0}</span><span>max {fmtDeaths(max)}</span><span>{y1}</span></div>
    </section>
  );
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
